import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  socialBadges,
  visibilityConfigs,
  usersWithBadges,
  badgeDashboardStats,
  getBadgeById,
  getBadgesByCategory,
  getTopBadgedUsers,
  type SocialBadge,
  type SocialBadgeId,
} from '@/mocks/rfwSocialBadges';

function BadgeDot({ badge, size = 'sm' }: { badge: SocialBadge; size?: 'sm' | 'md' }) {
  return (
    <div className="flex flex-col items-center gap-1.5 group cursor-default">
      <div className={`${size === 'md' ? 'w-14 h-14' : 'w-12 h-12'} rounded-2xl bg-gradient-to-r ${badge.gradient} flex items-center justify-center shadow-sm ${badge.glowColor} transition-transform duration-200 group-hover:scale-110`}>
        <i className={`${badge.icon} text-white ${size === 'md' ? 'text-xl' : 'text-lg'}`}></i>
      </div>
      <span className={`text-[10px] font-bold ${badge.textColor} text-center leading-tight`}>{badge.name}</span>
      {badge.exclusive && (
        <span className="text-[8px] text-amber-500 font-bold">★ EXCLUSIVA</span>
      )}
    </div>
  );
}

function UserHighlightCard({ user }: { user: typeof usersWithBadges[0] }) {
  const highlight = getBadgeById(user.highlightBadge);
  if (!highlight) return null;

  return (
    <div className={`p-4 rounded-2xl bg-background-50 border ${highlight.borderColor} hover:border-foreground-300/50 transition-all duration-200`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="relative shrink-0">
          <img src={user.photoUrl} alt={user.name} className="w-12 h-12 rounded-xl object-cover object-top" />
          <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-lg bg-gradient-to-r ${highlight.gradient} flex items-center justify-center shadow-sm ${highlight.glowColor}`}>
            <i className={`${highlight.icon} text-white text-[10px]`}></i>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-foreground-950 truncate">{user.name}</p>
          <p className="text-[10px] text-foreground-500">{user.username}</p>
          <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold mt-1 ${highlight.bgColor} ${highlight.textColor}`}>
            <i className={`${highlight.icon} text-[8px]`}></i>
            {highlight.shortName}
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-bold text-foreground-950">{user.badgeIds.length}</p>
          <p className="text-[9px] text-foreground-400">insignias</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        {user.badgeIds.filter((bid) => bid !== user.highlightBadge).slice(0, 4).map((bid) => {
          const b = getBadgeById(bid);
          if (!b) return null;
          return (
            <span key={bid} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8px] font-semibold ${b.bgColor} ${b.textColor}`}>
              <i className={`${b.icon} text-[7px]`}></i>{b.shortName}
            </span>
          );
        })}
      </div>
    </div>
  );
}

export default function InsigniasSection() {
  const [activeTab, setActiveTab] = useState<'galeria' | 'visibilidad' | 'usuarios'>('galeria');
  const topUsers = getTopBadgedUsers(6);

  const tabs = [
    { id: 'galeria' as const, label: 'Insignias', icon: 'ri-award-line' },
    { id: 'visibilidad' as const, label: 'Visibilidad', icon: 'ri-eye-line' },
    { id: 'usuarios' as const, label: 'Usuarios', icon: 'ri-group-line' },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
            INSIGNIAS Y ESTATUS SOCIALES
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Reconocimiento <span className="text-amber-600">Visible</span> para Cada Usuario
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            {badgeDashboardStats.totalBadges} insignias únicas con identidad de color propia. Visibles en perfil, comentarios, cabina, clubes, rankings y concursos.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Insignias', value: badgeDashboardStats.totalBadges.toString(), icon: 'ri-award-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Asignaciones', value: badgeDashboardStats.totalAssignments.toString(), icon: 'ri-shield-star-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'Más Común', value: badgeDashboardStats.mostCommonBadge, sub: `${badgeDashboardStats.mostCommonBadgeCount}`, icon: 'ri-group-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'Más Rara', value: badgeDashboardStats.rarestBadge, sub: `Solo ${badgeDashboardStats.rarestBadgeCount}`, icon: 'ri-star-line', color: 'text-fuchsia-600 bg-fuchsia-50' },
            { label: 'Promedio', value: `${badgeDashboardStats.averageBadgesPerUser}`, sub: 'por usuario', icon: 'ri-bar-chart-line', color: 'text-emerald-600 bg-emerald-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
                {stat.sub && <p className="text-[9px] text-foreground-400">{stat.sub}</p>}
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-amber-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Galería Tab */}
        {activeTab === 'galeria' && (
          <div className="space-y-6">
            {/* All 13 badges grid */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-3 md:gap-4">
              {socialBadges.map((badge) => (
                <BadgeDot key={badge.id} badge={badge} size="sm" />
              ))}
            </div>

            {/* Categories */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              {(['participacion', 'estatus', 'logro', 'rol'] as const).map((cat) => {
                const badgesInCat = getBadgesByCategory(cat);
                const catLabels: Record<string, { label: string; icon: string; color: string; bgColor: string }> = {
                  participacion: { label: 'Participación', icon: 'ri-flashlight-line', color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
                  estatus: { label: 'Estatus', icon: 'ri-shield-star-line', color: 'text-violet-600', bgColor: 'bg-violet-50' },
                  logro: { label: 'Logro', icon: 'ri-trophy-line', color: 'text-amber-600', bgColor: 'bg-amber-50' },
                  rol: { label: 'Rol', icon: 'ri-user-star-line', color: 'text-indigo-600', bgColor: 'bg-indigo-50' },
                };
                const info = catLabels[cat];
                return (
                  <div key={cat} className={`p-4 rounded-2xl ${info.bgColor} border border-background-200/70`}>
                    <div className="flex items-center gap-2 mb-3">
                      <i className={`${info.icon} ${info.color} text-base`}></i>
                      <span className={`text-xs font-bold ${info.color}`}>{info.label}</span>
                      <span className="text-[10px] text-foreground-400 ml-auto">{badgesInCat.length}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {badgesInCat.map((b) => (
                        <span key={b.id} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-semibold ${b.bgColor} ${b.textColor}`}>
                          <i className={`${b.icon} text-[8px]`}></i>{b.shortName}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Visibilidad Tab */}
        {activeTab === 'visibilidad' && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {visibilityConfigs.map((vis) => {
                const badgesHere = socialBadges.filter((b) => b.visibility.includes(vis.location));
                return (
                  <div key={vis.location} className="p-5 rounded-2xl bg-background-50 border border-background-200/70 transition-all duration-200 hover:border-amber-200/50">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                        <i className={`${vis.icon} text-amber-600 text-base`}></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-foreground-950">{vis.label}</h4>
                        <p className="text-[10px] text-foreground-500">{badgesHere.length} insignias</p>
                      </div>
                    </div>
                    <p className="text-xs text-foreground-500 leading-relaxed mb-3">{vis.description}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {badgesHere.map((b) => (
                        <span key={b.id} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-semibold ${b.bgColor} ${b.textColor}`}>
                          <i className={`${b.icon} text-[8px]`}></i>
                          {b.shortName}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Usuarios Tab */}
        {activeTab === 'usuarios' && (
          <div className="space-y-5">
            {/* Highlighted Users Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {topUsers.map((user) => (
                <UserHighlightCard key={user.id} user={user} />
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-10">
          <Link
            to="/insignias"
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-award-line"></i>
            Ver Todas las Insignias
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-6 rounded-2xl bg-amber-100/70 border border-amber-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-eye-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Las insignias deben ser fáciles de reconocer.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            Cada insignia tiene identidad visual propia: color único, degradado, ícono distintivo y glow. Se reconocen al instante en cualquier rincón de la plataforma sin necesidad de leer — solo con ver el color ya sabés quién está del otro lado.
          </p>
        </div>
      </div>
    </section>
  );
}